import { MouseEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import camelcaseKeys from "camelcase-keys";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";

import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import PendingIcon from "@mui/icons-material/Pending";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { visuallyHidden } from "@mui/utils";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Box,
  Collapse,
  Container,
  IconButton,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Tooltip,
} from "@mui/material";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import useOpen from "components/utils/useOpen";
import Overview from "components/rows/Overview";
import { useAuth } from "components/auth/AuthProvider";
import usePagination from "hooks/pagination";
import EmptyTableRow from "components/rows/Empty";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";
import LinearIndeterminate from "components/utils/LinearLoader";
import { usePavmAppliances, usePavmDisconnect } from "hooks/pavm/base";

type ProjectDetailProps = { collapsible?: boolean };

export const ProjectDetail = ({ collapsible = false }: ProjectDetailProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { open, toggle } = useOpen(true);
  const { user, setUser } = useAuth();

  const pavmActiveQueries = useIsFetching({
    predicate: (query) =>
      query.queryKey.some(
        (key) => typeof key === "string" && key.includes("PAVM")
      ),
  });
  const pavmIsFetching = pavmActiveQueries > 0;

  const paginate = usePagination({ defaultSort: "ip", defaultOrder: "asc" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const appliances = usePavmAppliances(body);
  const disconnect = usePavmDisconnect();

  const toProjectDetail = () =>
    navigate(`/projects/${user.project.name?.toLowerCase()}`);

  const handleDisconnect = () => {
    disconnect.mutate({
      uri: user.project.uri,
      email: user.email,
      projectId: user.project.id,
    });
  };

  useEffect(() => {
    if (disconnect.isSuccess) {
      const user = camelcaseKeys(disconnect.data.data.user, { deep: true });
      setUser(user);
      navigate("/");

      // if user disconnects from project, clear query cache
      queryClient.clear();
    }
  }, [disconnect.isSuccess]);

  useEffect(() => {
    if (user.project.name) {
      document.title = `GRACE | ${user.project.name.toLowerCase()}`;
    }
  }, [user.project]);

  useEffect(() => {
    if (!user.project.connected) navigate("/");
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      {appliances.isLoading ? (
        <LinearIndeterminate />
      ) : (
        <Container maxWidth="xl" sx={{ marginBottom: 4 }}>
          <SectionHeader
            title={user.project.name ?? ""}
            icon={
              <IconButton
                color="primary"
                onClick={toProjectDetail}
                sx={{ p: 0 }}
              >
                <AccountTreeIcon />
              </IconButton>
            }
            buttons={[
              <Button
                key={`project-disconnect`}
                color="error"
                variant="contained"
                disabled={pavmIsFetching}
                onClick={handleDisconnect}
                endIcon={
                  pavmIsFetching ? <PendingIcon /> : <RemoveCircleOutlineIcon />
                }
              >
                {pavmIsFetching ? <i>Loading</i> : "Disconnect"}
              </Button>,
              <TablePagination
                key={"appliances-pagination"}
                component="div"
                rowsPerPageOptions={[10, 25, 50, 100]}
                count={appliances?.data?.meta.total || 0}
                page={page}
                rowsPerPage={limit}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeLimit}
              />,
              <Refresh
                key={`appliances-refresh`}
                isFetching={appliances.isFetching}
                onClick={appliances.refetch}
              />,
              ...(collapsible
                ? [
                    <IconButton
                      key={"project-detail-collapse"}
                      size="small"
                      onClick={toggle}
                      aria-label="expand"
                      sx={{
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.3s",
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>,
                  ]
                : []),
            ]}
          />

          {/* MAIN DLM DATA TABLE */}
          <Collapse in={open}>
            <TableContainer sx={{ boxShadow: 4 }}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell />

                    {conf.COLUMNS.APPLIANCES.map((column) =>
                      column.key ? (
                        <TableCell
                          key={column.label}
                          sortDirection={order}
                          style={{ whiteSpace: "nowrap" }}
                          align={column.isHeader ? "left" : "right"}
                        >
                          <TableSortLabel
                            active={sort === column.key}
                            direction={sort === column.key ? order : "asc"}
                            onClick={(event: MouseEvent) =>
                              handleSort(event, column.key)
                            }
                          >
                            {column?.description ? (
                              <Tooltip title={column.description}>
                                <b>{column.label}</b>
                              </Tooltip>
                            ) : (
                              <b>{column.label}</b>
                            )}

                            {sort === column.key ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={column.label}
                          style={{ whiteSpace: "nowrap" }}
                          align={column.isHeader ? "left" : "right"}
                          sortDirection={order}
                        >
                          <b>{column.label}</b>
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {appliances?.data?.data.length === 0 ? (
                    <EmptyTableRow type="appliances" colSpan={10} />
                  ) : (
                    appliances?.data?.data.map((config) => (
                      <Overview key={config.host} config={config} />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Container>
      )}
    </ProtectedRoute>
  );
};
